import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import PersonaAvatar from '../../components/PersonaAvatar'

// ─────────────────────────────────────────────────────────────
// Feed — desktop Card stream for thegathering.space.
// Horizontal carousel: current card in center, neighbors peeking
// on either side. Swipe/scroll to browse, action buttons to process.
// ─────────────────────────────────────────────────────────────

interface FlipTarget {
  type: 'markdown' | 'project' | 'task' | 'url' | string
  value: string
}

interface FeedCard {
  id: string
  type: 'approval' | 'multi_choice' | 'text_input' | 'info'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  status: 'pending' | 'responded' | 'dismissed' | 'expired'
  title: string
  body: string
  from_persona: string
  project_id: string | null
  task_id: string | null
  options: (string | { label: string; value: string })[] | null
  image_url: string | null
  flip_target: FlipTarget | null
  created_at: string
  responded_at: string | null
  response: string | null
  expires_at: string | null
  category?: string
}

interface FeedPrefs {
  filters: { from_persona: string | null; type: string | null; priority: string | null; status: string | null }
  display: { card_size: string; auto_advance: boolean; show_processed: boolean }
  muted_sources: Record<string, { muted_at: string }>
  muted_categories: Record<string, { muted_at: string }>
  version: number
}

const PRIORITY_COLORS: Record<string, string> = {
  urgent: '#f85149', high: '#EE6802', medium: '#856FC8', low: '#5a5a64',
}

const TYPE_LABELS: Record<string, string> = {
  approval: 'Approval', multi_choice: 'Choice', text_input: 'Response', info: 'FYI',
}

function relativeTime(iso: string): string {
  if (!iso) return ''
  const then = new Date(iso.replace(' ', 'T') + (iso.includes('Z') ? '' : 'Z')).getTime()
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function renderMarkdown(md: string): string {
  if (!md) return ''
  let html = md.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  html = html.replace(/^###### (.*)$/gm, '<h6>$1</h6>')
  html = html.replace(/^##### (.*)$/gm, '<h5>$1</h5>')
  html = html.replace(/^#### (.*)$/gm, '<h4>$1</h4>')
  html = html.replace(/^### (.*)$/gm, '<h3>$1</h3>')
  html = html.replace(/^## (.*)$/gm, '<h2>$1</h2>')
  html = html.replace(/^# (.*)$/gm, '<h1>$1</h1>')
  html = html.replace(/```([\s\S]*?)```/g, (_m, code) => `<pre><code>${code.trim()}</code></pre>`)
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>')
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>')
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text, url) => {
    const safeUrl = /^https?:\/\//i.test(url) ? url : '#'
    return `<a href="${safeUrl}" target="_blank" rel="noopener">${text}</a>`
  })
  html = html.replace(/^[-*] (.+)$/gm, '<li>$1</li>')
  html = html.replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`)
  html = html.split(/\n{2,}/).map(block => {
    if (/^<(h\d|ul|pre|ol|blockquote)/.test(block.trim())) return block
    return `<p>${block.trim().replace(/\n/g, '<br/>')}</p>`
  }).join('\n')
  return html
}

// ─────────────────────────────────────────────────────────────

export default function FeedPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [index, setIndex] = useState(0)
  const [flipped, setFlipped] = useState(false)
  const [actionPending, setActionPending] = useState(false)
  const [comment, setComment] = useState('')
  const [starScore, setStarScore] = useState(0)
  const [showList, setShowList] = useState(false)

  const { data: prefs } = useQuery<FeedPrefs>({
    queryKey: ['feedPrefs'],
    queryFn: async () => { const r = await fetch('/api/feed/preferences'); return r.json() },
    staleTime: 30_000,
  })
  const savePrefs = useMutation({
    mutationFn: async (next: Partial<FeedPrefs>) => {
      const r = await fetch('/api/feed/preferences', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      })
      return r.json()
    },
    onSuccess: (data) => qc.setQueryData(['feedPrefs'], data),
  })
  const filters = prefs?.filters || { from_persona: null, type: null, priority: null, status: 'pending' }

  // Feed fetch — 5s refetch so cards processed on iPhone vanish quickly on desktop
  const { data: feedData, isLoading, refetch } = useQuery<{ cards: FeedCard[] }>({
    queryKey: ['feedCards', filters, prefs?.muted_sources, prefs?.muted_categories],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('user', 'joel')
      if (filters.status) params.set('status', filters.status)
      if (filters.from_persona) params.set('persona', filters.from_persona)
      if (filters.type) params.set('type', filters.type)
      if (filters.priority) params.set('priority', filters.priority)
      params.set('limit', '1000')
      const r = await fetch(`/api/feed?${params}`)
      return r.json()
    },
    refetchInterval: 5_000,
  })

  // Fetch all cards (any status) for filter dropdown options — never filters out processed personas
  const { data: allCardsData } = useQuery<{ cards: FeedCard[] }>({
    queryKey: ['feedCardsMeta'],
    queryFn: async () => {
      const r = await fetch('/api/feed?user=joel&limit=1000&status=')
      return r.json()
    },
    staleTime: 60_000,
  })
  const allPersonas = [...new Set((allCardsData?.cards || []).map(c => c.from_persona).filter(Boolean))].sort() as string[]
  const allCategories = [...new Set((allCardsData?.cards || []).map(c => c.category).filter(Boolean))].sort() as string[]

  const cards = feedData?.cards || []
  const card = cards[index]

  useEffect(() => {
    if (index >= cards.length && cards.length > 0) setIndex(cards.length - 1)
    if (cards.length === 0) setIndex(0)
  }, [cards.length, index])

  useEffect(() => { setFlipped(false); setComment(''); setStarScore(0) }, [card?.id])

  // Prev / next cards for the 3-panel view (no scroll container)
  const prevCard = index > 0 ? cards[index - 1] : null
  const nextCard = index < cards.length - 1 ? cards[index + 1] : null

  function advance(dir: 1 | -1) {
    setIndex(i => {
      const n = Math.max(0, Math.min(cards.length - 1, i + dir))
      return n
    })
    setFlipped(false)
  }

  // Keyboard navigation
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (e.key === 'ArrowRight' || e.key === 'j' || e.key === 'J') advance(1)
      else if (e.key === 'ArrowLeft' || e.key === 'k' || e.key === 'K') advance(-1)
      else if (e.key === 'f' || e.key === 'F' || e.key === ' ') { e.preventDefault(); setFlipped(v => !v) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [cards.length])

  async function runAction(path: string, body?: any) {
    if (!card) return
    setActionPending(true)
    try {
      const r = await fetch(`/api/feed/cards/${card.id}${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      })
      if (!r.ok) throw new Error(await r.text())
      setComment('')
      const result = await refetch()
      const newCards = result.data?.cards || []
      // Clamp index to the new (shorter) card list
      setIndex(i => Math.min(i, Math.max(0, newCards.length - 1)))
    } catch (e) { console.error('[feed-action]', e) }
    finally { setActionPending(false) }
  }

  // Respond to approval cards via PATCH (matching iOS — carries score + note, delivers to Tack)
  async function respondToCard(action: 'approved' | 'rejected') {
    if (!card) return
    setActionPending(true)
    try {
      const body: any = { action, user: 'joel' }
      if (starScore > 0) body.score = starScore
      const c = comment.trim()
      if (c) body.note = c
      const r = await fetch(`/api/feed/cards/${card.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!r.ok) throw new Error(await r.text())
      setComment(''); setStarScore(0)
      const result = await refetch()
      setIndex(i => Math.min(i, Math.max(0, (result.data?.cards?.length || 1) - 1)))
    } catch (e) { console.error('[respond]', e) }
    finally { setActionPending(false) }
  }
  const approve = () => respondToCard('approved')
  const deny = () => respondToCard('rejected')
  const iterate = () => respondToCard('iterate' as 'approved')
  const saveCard = () => respondToCard('save' as 'approved')
  const dismiss = async () => {
    if (!card) return
    setActionPending(true)
    try {
      const r = await fetch(`/api/feed/cards/${card.id}`, { method: 'DELETE' })
      if (!r.ok) throw new Error(await r.text())
      setComment('')
      const result = await refetch()
      setIndex(i => Math.min(i, Math.max(0, (result.data?.cards?.length || 1) - 1)))
    } catch (e) { console.error('[dismiss]', e) }
    finally { setActionPending(false) }
  }
  const park = () => runAction('/park', { user: 'joel' })
  const snooze = () => runAction('/snooze', { user: 'joel' })

  function setFilter(key: keyof typeof filters, value: string | null) {
    savePrefs.mutate({ filters: { ...filters, [key]: value } })
  }

  async function openChatWith(persona: string) {
    try {
      const r = await fetch('/api/conversations', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ find_or_create: true, participants: ['joel', persona] }),
      })
      const data = await r.json()
      if (data?.id) navigate(`/conversation/${data.id}`)
    } catch (e) { console.error('[chat-open]', e) }
  }

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-base)', color: 'var(--text-primary)', display: 'flex', flexDirection: 'column' }}>
      {/* Filter bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px',
        borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)', flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Feed</span>
        <FilterChip label="Pending" active={filters.status === 'pending'} onClick={() => setFilter('status', 'pending')} />
        <FilterChip label="Responded" active={filters.status === 'responded'} onClick={() => setFilter('status', 'responded')} />
        <FilterChip label="All" active={!filters.status} onClick={() => setFilter('status', null)} />
        <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px' }} />
        <FilterChip label="All types" active={!filters.type} onClick={() => setFilter('type', null)} />
        <FilterChip label="Approvals" active={filters.type === 'approval'} onClick={() => setFilter('type', 'approval')} />
        <FilterChip label="Choice" active={filters.type === 'multi_choice'} onClick={() => setFilter('type', 'multi_choice')} />
        <FilterChip label="Info" active={filters.type === 'info'} onClick={() => setFilter('type', 'info')} />
        <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px' }} />
        <FilterChip label="Any priority" active={!filters.priority} onClick={() => setFilter('priority', null)} />
        <FilterChip label="Urgent" active={filters.priority === 'urgent'} onClick={() => setFilter('priority', 'urgent')} />
        <FilterChip label="High" active={filters.priority === 'high'} onClick={() => setFilter('priority', 'high')} />
        <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px' }} />
        <select
          value={filters.from_persona || ''}
          onChange={e => setFilter('from_persona', e.target.value || null)}
          style={{
            fontSize: 11, color: filters.from_persona ? 'var(--accent)' : 'var(--text-muted)',
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '3px 8px', cursor: 'pointer', outline: 'none',
          }}
        >
          <option value="">All personas</option>
          {allPersonas.map(p => (
            <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
          ))}
        </select>
        {allCategories.length > 1 && (
          <select
            value={(filters as any).category || ''}
            onChange={e => setFilter('category' as any, e.target.value || null)}
            style={{
              fontSize: 11, color: (filters as any).category ? 'var(--accent)' : 'var(--text-muted)',
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: 6, padding: '3px 8px', cursor: 'pointer', outline: 'none',
            }}
          >
            <option value="">All categories</option>
            {allCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center', position: 'relative' }}>
          <button
            onClick={() => setShowList(v => !v)}
            style={{
              fontSize: 11, color: 'var(--text-muted)', background: 'transparent',
              border: '1px solid var(--border)', borderRadius: 6, padding: '3px 10px',
              cursor: 'pointer',
            }}
            title="Click to see card list"
          >
            {cards.length > 0 ? `${index + 1} / ${cards.length}` : '0 cards'}
          </button>
          {cards.length > 1 && index < cards.length - 1 && (
            <button
              onClick={() => setIndex(cards.length - 1)}
              style={{
                fontSize: 11, color: 'var(--accent)', background: 'transparent',
                border: '1px solid var(--accent)', borderRadius: 6, padding: '3px 10px',
                cursor: 'pointer',
              }}
              title="Jump to newest card"
            >
              Latest →
            </button>
          )}
          <Link to="/feed/settings" style={{
            fontSize: 11, color: 'var(--text-secondary)', textDecoration: 'none',
            padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 6,
          }}>⚙ Settings</Link>
          {showList && <CardListPopup cards={cards} currentIndex={index} onSelect={(i) => { setIndex(i); setShowList(false) }} onClose={() => setShowList(false)} />}
        </div>
      </div>

      {/* Action bar — above the carousel for easy access */}
      {card && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
          padding: '12px 20px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)',
        }}>
          {/* Star rating — approval cards only */}
          {card.type === 'approval' && card.status === 'pending' && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 2 }}>
              {[1,2,3,4,5].map(s => (
                <button key={s} onClick={() => setStarScore(s)} style={{
                  background: 'transparent', border: 'none', cursor: 'pointer', padding: 2,
                  fontSize: 24, color: s <= starScore ? '#FBBF24' : '#444444',
                  transition: 'color 0.15s, transform 0.1s',
                  transform: s <= starScore ? 'scale(1.1)' : 'scale(1)',
                }} title={`${s} star${s > 1 ? 's' : ''}`}>
                  {s <= starScore ? '★' : '☆'}
                </button>
              ))}
              {starScore > 0 && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>{starScore}/5</span>}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            {card.type === 'approval' && (
              <>
                <ActionButton onClick={deny} disabled={actionPending || card.status !== 'pending'} tone="deny">Reject</ActionButton>
                <ActionButton onClick={approve} disabled={actionPending || card.status !== 'pending'} tone="approve">Approve</ActionButton>
              </>
            )}
            {card.type === 'multi_choice' && card.options?.map((opt) => {
              const label = typeof opt === 'string' ? opt : opt.label
              const value = typeof opt === 'string' ? opt : opt.value
              return <ActionButton key={label} onClick={() => runAction('/process', { response: value, user: 'joel' })} disabled={actionPending || card.status !== 'pending'} tone="neutral">{label}</ActionButton>
            })}
            {card.type === 'text_input' && <TextResponseControls card={card} onSubmit={(t) => runAction('/process', { response: t, user: 'joel' })} pending={actionPending} />}
            {card.type === 'info' && <ActionButton onClick={() => runAction('/process', { response: comment.trim() ? `acknowledged: ${comment.trim()}` : 'acknowledged', user: 'joel' })} disabled={actionPending || card.status !== 'pending'} tone="neutral">Got it</ActionButton>}
            {(card.type === 'approval' || card.type === 'multi_choice') && (
              <>
                <ActionButton onClick={iterate} disabled={actionPending || card.status !== 'pending'} tone="neutral">Iterate</ActionButton>
                <ActionButton onClick={saveCard} disabled={actionPending || card.status !== 'pending'} tone="neutral">Save</ActionButton>
              </>
            )}
            <span style={{ width: 1, height: 22, background: 'var(--border)', margin: '0 4px' }} />
            <ActionButton onClick={() => setFlipped(v => !v)} tone="ghost">{flipped ? 'Front' : 'Flip'}</ActionButton>
            <ActionButton onClick={park} disabled={actionPending} tone="ghost">Park</ActionButton>
            <ActionButton onClick={snooze} disabled={actionPending} tone="ghost">Snooze</ActionButton>
            <ActionButton onClick={dismiss} disabled={actionPending} tone="ghost">Dismiss</ActionButton>
            {card.project_id && <Link to={`/project/${card.project_id}`} style={{ padding: '6px 12px', fontSize: 12, textDecoration: 'none', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 6 }}>Open project →</Link>}
          </div>
          {(card.type === 'approval' || card.type === 'multi_choice' || card.type === 'info') && card.status === 'pending' && (
            <input
              type="text" value={comment} onChange={e => setComment(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && comment.trim() && card.type === 'approval') approve() }}
              placeholder="Optional comment — sent with approve, deny, iterate, or save…"
              style={{
                width: 'min(560px, 90vw)', padding: '7px 12px', fontSize: 12,
                background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                border: '1px solid var(--border)', borderRadius: 6, outline: 'none',
              }}
            />
          )}
        </div>
      )}

      {/* 3-panel carousel — prev | current | next. No scroll container. */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, position: 'relative', overflow: 'hidden', padding: '16px 60px' }}>
        {isLoading && cards.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading feed…</div>}
        {!isLoading && cards.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 28, marginBottom: 10, color: 'var(--text-primary)' }}>
              {filters.from_persona ? `No more cards from ${filters.from_persona.charAt(0).toUpperCase() + filters.from_persona.slice(1)}.` : 'Feed is clear.'}
            </div>
            <div style={{ fontSize: 13 }}>
              {(filters.from_persona || (filters as any).category)
                ? <button onClick={() => { setFilter('from_persona', null); setFilter('category' as any, null) }} style={{ color: 'var(--accent)', background: 'none', border: '1px solid var(--accent)', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 13 }}>Clear filters</button>
                : <>No cards match these filters. Adjust filters or unmute in <Link to="/feed/settings" style={{ color: 'var(--accent)' }}>Settings</Link>.</>
              }
            </div>
          </div>
        )}
        {cards.length > 0 && (
          <>
            <button onClick={() => advance(-1)} disabled={index === 0} style={navArrowStyle('left', index === 0)} aria-label="Previous">‹</button>
            <button onClick={() => advance(1)} disabled={index >= cards.length - 1} style={navArrowStyle('right', index >= cards.length - 1)} aria-label="Next">›</button>

            {/* Previous card peek */}
            {prevCard ? (
              <div onClick={() => advance(-1)} style={{
                flexShrink: 0, width: 'min(200px, 15vw)', height: 'min(580px, 66vh)',
                opacity: 0.45, transform: 'scale(0.88)', cursor: 'pointer',
                position: 'relative', transition: 'opacity 0.3s',
              }}>
                <CardFace back={false}><CardFront card={prevCard} onPersonaClick={() => {}} /></CardFace>
              </div>
            ) : <div style={{ flexShrink: 0, width: 'min(200px, 15vw)' }} />}

            {/* Current card — full size, flippable */}
            <div
              onDoubleClick={() => setFlipped(v => !v)}
              style={{
                flexShrink: 0, width: 'min(560px, 55vw)', height: 'min(620px, 68vh)',
                position: 'relative',
              }}
            >
              <div style={{
                width: '100%', height: '100%',
                transition: 'transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1)',
                transformStyle: 'preserve-3d',
                transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                position: 'relative',
              }}>
                <CardFace back={false}><CardFront card={card} onPersonaClick={openChatWith} /></CardFace>
                <CardFace back={true}><CardBack card={card} /></CardFace>
              </div>
            </div>

            {/* Next card peek */}
            {nextCard ? (
              <div onClick={() => advance(1)} style={{
                flexShrink: 0, width: 'min(200px, 15vw)', height: 'min(580px, 66vh)',
                opacity: 0.45, transform: 'scale(0.88)', cursor: 'pointer',
                position: 'relative', transition: 'opacity 0.3s',
              }}>
                <CardFace back={false}><CardFront card={nextCard} onPersonaClick={() => {}} /></CardFace>
              </div>
            ) : <div style={{ flexShrink: 0, width: 'min(200px, 15vw)' }} />}
          </>
        )}
      </div>

      <div style={{ textAlign: 'center', padding: '4px 0 10px', fontSize: 10, color: 'var(--text-muted)' }}>
        scroll or swipe to browse · <kbd style={kbd}>J</kbd>/<kbd style={kbd}>K</kbd> advance · <kbd style={kbd}>F</kbd> flip · buttons above to process
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────

function CardFace({ back, children }: { back: boolean; children: React.ReactNode }) {
  return (
    <div style={{
      position: 'absolute', inset: 0,
      backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
      borderRadius: 16, overflow: 'hidden',
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      boxShadow: '0 24px 64px -12px rgba(0,0,0,0.5)',
      transform: back ? 'rotateY(180deg)' : undefined,
      display: 'flex', flexDirection: 'column',
    }}>
      {children}
    </div>
  )
}

// Mirror the iOS showsSummaryOnFront logic: if flip_target is markdown and
// contains no vault paths (05 - System/...), show the summary on front and
// the raw body on back. This matches Joel's 2026-04-15 directive.
function showsSummaryOnFront(card: FeedCard): boolean {
  if (!card.flip_target || card.flip_target.type !== 'markdown') return false
  const v = card.flip_target.value || ''
  // Vault path heuristic: same as iOS MarkdownRenderer.extractVaultPaths
  return !(/\d{2} - /.test(v))
}

function CardFront({ card, onPersonaClick }: { card: FeedCard; onPersonaClick: (p: string) => void }) {
  const priorityColor = PRIORITY_COLORS[card.priority] || PRIORITY_COLORS.medium
  const summaryMode = showsSummaryOnFront(card)
  return (
    <>
      <div style={{
        flexShrink: 0, padding: '20px 24px 16px',
        background: `linear-gradient(135deg, ${priorityColor}26 0%, transparent 100%)`,
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
          <button
            onClick={(e) => { e.stopPropagation(); onPersonaClick(card.from_persona) }}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: '6px 8px', borderRadius: 8, color: 'var(--text-primary)',
              flex: 1, textAlign: 'left', minWidth: 0,
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            title={`Open 1:1 with ${card.from_persona}`}
          >
            <PersonaAvatar name={card.from_persona} size={56} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 18, fontWeight: 600, textTransform: 'capitalize', letterSpacing: '-0.2px' }}>{card.from_persona}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>→ open chat</span>
              </div>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)' }}>
                {TYPE_LABELS[card.type] || card.type} · {relativeTime(card.created_at)}
                {card.category && <> · {card.category}</>}
              </div>
            </div>
          </button>
          <div style={{
            fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase',
            padding: '5px 12px', borderRadius: 12,
            background: `${priorityColor}33`, color: priorityColor,
            border: `1px solid ${priorityColor}66`, flexShrink: 0,
          }}>
            {card.priority}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, padding: '20px 26px 24px', overflowY: 'auto' }}>
        <h2 style={{
          fontFamily: 'Playfair Display, serif', fontWeight: 700,
          fontSize: 22, lineHeight: 1.25, marginBottom: 14, color: 'var(--text-primary)',
        }}>
          {card.title}
        </h2>
        {/* Summary mode: flip_target markdown on front, raw body on back */}
        {summaryMode ? (
          <div className="feed-markdown" style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)' }}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(card.flip_target!.value) }}
          />
        ) : (
          <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
            {card.body}
          </div>
        )}
        {summaryMode && (
          <div style={{ marginTop: 14, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Double-click or press F to see the full text →
          </div>
        )}
        {card.status !== 'pending' && (
          <div style={{
            marginTop: 18, padding: '10px 14px',
            background: 'var(--bg-elevated)', borderRadius: 8,
            fontSize: 12, color: 'var(--text-muted)',
          }}>
            <strong style={{ color: 'var(--text-secondary)' }}>{card.status}</strong>
            {card.response && <> — {card.response}</>}
            {card.responded_at && <> · {relativeTime(card.responded_at)}</>}
          </div>
        )}
      </div>
    </>
  )
}

function CardBack({ card }: { card: FeedCard }) {
  const ft = card.flip_target
  const summaryMode = showsSummaryOnFront(card)
  return (
    <div style={{ flex: 1, padding: '24px 30px', overflowY: 'auto', fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 12 }}>
        {summaryMode ? 'FULL TEXT' : (ft?.type || 'detail').toUpperCase()} · {card.from_persona}
      </div>
      {/* Summary mode: back shows the raw body (the original idea) */}
      {summaryMode && card.body ? (
        <div className="feed-markdown" dangerouslySetInnerHTML={{ __html: renderMarkdown(card.body) }} />
      ) : (
        <>
          {!ft && <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No additional detail. Double-click or press F to flip back.</div>}
          {ft?.type === 'markdown' && <div className="feed-markdown" dangerouslySetInnerHTML={{ __html: renderMarkdown(ft.value) }} />}
          {ft?.type === 'project' && <Link to={`/project/${ft.value}`} style={{ color: 'var(--accent)', textDecoration: 'underline' }}>Open project {ft.value} →</Link>}
          {ft?.type === 'task' && <div>Linked task: <code>{ft.value}</code></div>}
          {ft?.type === 'url' && <a href={ft.value} target="_blank" rel="noopener" style={{ color: 'var(--accent)' }}>{ft.value}</a>}
          {ft && !['markdown', 'project', 'task', 'url'].includes(ft.type) && <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(ft, null, 2)}</pre>}
        </>
      )}
    </div>
  )
}

function TextResponseControls({ card, onSubmit, pending }: { card: FeedCard; onSubmit: (t: string) => void; pending: boolean }) {
  const [text, setText] = useState('')
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <input type="text" value={text} onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && text.trim()) onSubmit(text.trim()) }}
        placeholder="Type your response…" disabled={pending || card.status !== 'pending'}
        style={{ width: 280, padding: '7px 12px', fontSize: 13, background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 6, outline: 'none' }}
      />
      <ActionButton onClick={() => { if (text.trim()) onSubmit(text.trim()) }} disabled={pending || !text.trim() || card.status !== 'pending'} tone="approve">Send</ActionButton>
    </div>
  )
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 10px', fontSize: 11, fontWeight: 500,
      background: active ? 'var(--accent)' : 'var(--bg-elevated)',
      color: active ? '#fff' : 'var(--text-secondary)',
      border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
      borderRadius: 12, cursor: 'pointer', transition: 'all 0.15s',
    }}>{label}</button>
  )
}

function ActionButton({ children, onClick, disabled, tone }: { children: React.ReactNode; onClick: () => void; disabled?: boolean; tone: 'approve' | 'deny' | 'neutral' | 'ghost' }) {
  const bg = tone === 'approve' ? '#238636' : tone === 'deny' ? '#b8324a' : tone === 'neutral' ? 'var(--accent)' : 'transparent'
  const color = tone === 'ghost' ? 'var(--text-secondary)' : '#fff'
  const border = tone === 'ghost' ? '1px solid var(--border)' : '1px solid transparent'
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: '7px 16px', fontSize: 12, fontWeight: 600, background: bg, color, border, borderRadius: 6,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, transition: 'all 0.15s',
    }}>{children}</button>
  )
}

function navArrowStyle(side: 'left' | 'right', disabled: boolean): React.CSSProperties {
  return {
    position: 'absolute', [side]: 16, top: '50%', transform: 'translateY(-50%)',
    width: 44, height: 44, borderRadius: 22,
    background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
    border: '1px solid var(--border)', cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 22, lineHeight: 1, opacity: disabled ? 0.3 : 0.85, zIndex: 10,
  }
}

function CardListPopup({ cards, currentIndex, onSelect, onClose }: {
  cards: FeedCard[]; currentIndex: number; onSelect: (i: number) => void; onClose: () => void
}) {
  const listRef = useRef<HTMLDivElement>(null)
  // Scroll to current card on open
  useEffect(() => {
    const el = listRef.current?.children[currentIndex] as HTMLElement | undefined
    el?.scrollIntoView({ block: 'center' })
  }, [currentIndex])
  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: '10%', left: '50%', transform: 'translateX(-50%)',
        width: 'min(520px, 90vw)', maxHeight: '75vh',
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        zIndex: 101, display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Card List ({cards.length})</span>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
          {cards.map((c, i) => (
            <button
              key={c.id}
              onClick={() => onSelect(i)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                padding: '10px 18px', textAlign: 'left',
                background: i === currentIndex ? 'var(--bg-elevated)' : 'transparent',
                border: 'none', cursor: 'pointer', color: 'var(--text-primary)',
                borderLeft: i === currentIndex ? '3px solid var(--accent)' : '3px solid transparent',
              }}
            >
              <PersonaAvatar name={c.from_persona} size={28} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.title}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  {c.from_persona} · {TYPE_LABELS[c.type] || c.type} · {c.priority}
                  {c.category && ` · ${c.category}`}
                </div>
              </div>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 }}>{relativeTime(c.created_at)}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  )
}

const kbd: React.CSSProperties = {
  display: 'inline-block', padding: '1px 5px',
  background: 'var(--bg-elevated)', border: '1px solid var(--border)',
  borderRadius: 3, fontFamily: 'monospace', fontSize: 10, color: 'var(--text-secondary)',
}
